import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Copy, CheckSquare, Square, EyeOff, Package } from 'lucide-react';
import { useAnchoredMenu, WORKBENCH_MENU_TOP_INSET } from '../hooks/useAnchoredMenu';

interface EdgeContextMenuProps {
    edge: any;
    position: { x: number; y: number };
    onClose: () => void;
    onSelect: (edge: any) => void;
    onDeselect: (edge: any) => void;
    isSelected?: boolean;
    onDeemphasize: (edgeId: string) => void;
    onClearEmphasis: (edgeId: string) => void;
    onStash?: () => void;
}

export const EdgeContextMenu: React.FC<EdgeContextMenuProps> = ({
    edge,
    position,
    onClose,
    onSelect,
    onDeselect,
    isSelected,
    onDeemphasize,
    onClearEmphasis,
    onStash
}) => {
    const edgeId = edge?.id || edge?.tx_hash;
    const txHash = edge?.tx_hash || edge?.id;
    // Edges are anchored at their midpoint, which lands near a viewport edge as
    // often as nodes do — keep clear of the Workbench header like node menus.
    const { ref, style } = useAnchoredMenu<HTMLDivElement>(position, {
        insets: { top: WORKBENCH_MENU_TOP_INSET },
    });

    useEffect(() => {
        const blockOsMenu = (event: Event) => {
            event.preventDefault();
        };
        document.addEventListener('contextmenu', blockOsMenu, true);
        return () => document.removeEventListener('contextmenu', blockOsMenu, true);
    }, []);

    return createPortal(
        <>
        <div
            className="fixed inset-0 z-[9998]"
            data-testid="edge-context-menu-backdrop"
            onClick={onClose}
            onContextMenu={(e) => {
                e.preventDefault();
                // Do NOT onClose() — same-gesture contextmenu after cxttap/mouseup
                // would dismiss the menu we just opened (see NodeContextMenu).
            }}
        />
        <div
            ref={ref}
            className="z-[9999] bg-popover border border-border rounded-lg shadow-lg p-2 text-xs min-w-[180px] styled-scrollbar"
            style={{ ...style, zIndex: 9999 }}
            data-testid="edge-context-menu"
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
        >
            <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">Edge Actions</div>

            {/* Primary action: Select/Deselect — toggles both evidence checkbox and canvas emphasis */}
            {isSelected ? (
                <button
                    onClick={() => {
                        onDeselect(edge);
                        onClose();
                    }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-muted rounded text-blue-400"
                >
                    <CheckSquare className="w-3 h-3" />
                    Deselect
                </button>
            ) : (
                <button
                    onClick={() => {
                        onSelect(edge);
                        onClose();
                    }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-muted rounded"
                >
                    <Square className="w-3 h-3" />
                    Select
                </button>
            )}

            <button
                onClick={() => {
                    if (edgeId) onDeemphasize(edgeId);
                    onClose();
                }}
                className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-muted rounded"
            >
                <EyeOff className="w-3 h-3" />
                De-emphasize
            </button>
            <button
                onClick={() => {
                    if (edgeId) onClearEmphasis(edgeId);
                    onClose();
                }}
                className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-muted rounded"
            >
                Clear
            </button>

            <div className="h-px bg-border my-1" />

            <button
                onClick={() => {
                    if (txHash) {
                        navigator.clipboard.writeText(txHash);
                    }
                    onClose();
                }}
                className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-muted rounded"
            >
                <Copy className="w-3 h-3" />
                Copy Tx Hash
            </button>

            {onStash && (
                <>
                    <div className="h-px bg-border my-1" />
                    <button
                        onClick={() => {
                            onStash();
                            onClose();
                        }}
                        className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-muted rounded text-amber-400"
                    >
                        <Package className="w-3 h-3" />
                        Stash Edge
                    </button>
                </>
            )}
        </div>
        </>,
        document.body,
    );
};
